import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon, type IconName } from './Icon';

/** A floor note: its text, an action beside it, and how long it stays. A new `key` shows it again. */
export interface FloorNoticeValue { key: number; text: string; action?: { label: string; run(): void }; ms: number; icon?: IconName }

/**
 * A short note about the floors: the floor the walk has climbed to, whose name the app's balloon shows for two seconds
 * (WalkView.CommitStairLevelSwitch), or the floor a flight of stairs added over it. Like the app's balloon it sits at the
 * bottom of what the panels leave of the stage, clear of the loading note at the top (`locate`: the bottom middle of that
 * area, in the window). It goes by itself, or on a click; the pointer or the focus on it holds it, and it fades out
 * without taking clicks as it goes.
 */
export function FloorNotice({ notice, locate, onDone }: { notice: FloorNoticeValue; locate(): { x: number; y: number }; onDone(): void }) {
  const [leaving, setLeaving] = useState(false);
  const [place, setPlace] = useState<{ x: number; y: number } | null>(null);
  const held = useRef(false), timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const done = useRef(onDone); done.current = onDone;
  const where = useRef(locate); where.current = locate;
  const wait = (ms: number) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (!held.current) setLeaving(true); }, ms);
  };
  useEffect(() => {
    setLeaving(false); held.current = false; wait(notice.ms);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [notice.key]);
  // Placed once the panels the same action opens are laid out, and again on their next frame and as the window changes.
  useLayoutEffect(() => {
    const measure = () => setPlace(where.current());
    measure();
    const frame = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', measure); };
  }, [notice.key]);
  useEffect(() => {
    if (!leaving) return;
    const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    const gone = setTimeout(() => done.current(), reduced ? 0 : 160);
    return () => clearTimeout(gone);
  }, [leaving]);
  const hold = () => { held.current = true; if (timer.current) clearTimeout(timer.current); };
  const release = () => { held.current = false; wait(1500); };
  return <div className="floor-notice-dock" style={place ? { left: place.x, top: place.y } : { visibility: 'hidden' }}>
    <div className={`banner info floor-notice${leaving ? ' is-leaving' : ''}`} role="status" aria-live="polite"
      onClick={() => setLeaving(true)} onPointerEnter={hold} onPointerLeave={release} onFocus={hold} onBlur={release}>
      <Icon name={notice.icon ?? 'layers'} size={16} /><span>{notice.text}</span>
      {notice.action && <button type="button" className="text-button" onClick={event => { event.stopPropagation(); notice.action!.run(); setLeaving(true); }}>{notice.action.label}</button>}
    </div>
  </div>;
}
